import { type KeyboardEvent } from 'react';
import { Editable, useSlate } from 'slate-react';
import type { EditableProps } from 'slate-react/dist/components/editable';
import type { Editor as SlateEditor } from 'slate';
import { useRefs } from '@/scripts/hooks';
import type { CustomDescendant } from './types';
import { useEditorContext } from './scripts/context';

export interface EditorProps extends Omit<EditableProps, 'onChange'> {
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  onChange?: (value: CustomDescendant[], editor: SlateEditor) => void;
}

export default function Editor({
  className,
  children,
  onChange,
  onKeyDown: onKeyDown_user,
  ...props
}: EditorProps) {
  const {
    editableProps: { editableRef, onKeyDown: onKeyDown_context, ...editableProps },
  } = useEditorContext();

  const editor = useSlate();

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      onKeyDown_context?.(event);
      if (!event.defaultPrevented) {
        onKeyDown_user?.(event);
      }
    },
    [onKeyDown_user, onKeyDown_context]
  );

  const refs = useRefs({ onChange, editor });

  useLayoutEffect(() => {
    const { onChange, editor } = refs.current;
    onChange?.(editor.children, editor);
  }, [refs, editor.children]);

  return (
    <Editable
      ref={editableRef}
      className={cls(className, 'slate-editor')}
      onKeyDown={onKeyDown}
      {...editableProps}
      {...props}
    />
  );
}
